import {
  Box,
  Button,
  Divider,
  GlobalStyles,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PrintIcon from '@mui/icons-material/Print'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useCompanies, useKitchenSlip } from '@/api/hooks'
import { useAuthStore } from '@/auth/authStore'
import type { KitchenSlip, SlipDish, SlipSitting } from '@/api/types'
import { EmptyState, ErrorBanner, InlineSpinner, formatDate, humanise } from '@/components/common'

type Language = 'en' | 'ta'

/**
 * The sheet the kitchen works from when the food goes out.
 *
 * <p>One A4 page per sitting, and on each page the same slip twice - the left
 * half and the right half, cut down the middle. One copy travels with the food
 * and one stays behind, and both are signed by the three people who are
 * answerable for it. A sitting is what a cook works to, so a page that mixed
 * the breakfast and the dinner would be carried around all day by whoever was
 * doing neither.</p>
 *
 * <p>Printed from the browser rather than drawn as a PDF on the server, for the
 * same reason the menu sheet is: the dishes are named in Tamil, Tamil needs its
 * vowel signs reordered before they are drawn, and a browser does that
 * correctly where the PDF library does not.</p>
 */

/** Everything on the sheet that is not a name from the catalogue. */
const TEXT: Record<Language, Record<string, string>> = {
  en: {
    title: 'Kitchen slip',
    order: 'Order',
    outlet: 'Outlet',
    customer: 'Customer',
    contact: 'Contact',
    orderDate: 'Order date',
    eventDate: 'Event date',
    service: 'Service',
    occasion: 'Occasion',
    pax: 'Pax',
    session: 'Session',
    packageLabel: 'Package',
    packageItems: 'Package items',
    extraItems: 'Items ordered separately',
    parcels: 'Parcels',
    parcel: 'Parcel',
    multiParcel: 'MULTI PARCEL',
    parcelsNotSet: 'parcels not written down yet',
    address: 'Delivery address',
    instructions: 'Special instructions',
    note: 'Note',
    live: 'live',
    leaf: 'Leaf service',
    buffet: 'Buffet',
    none: 'Nothing on this sitting yet',
    nothing: 'Nothing to dispatch on this order yet.',
    print: 'Print',
    back: 'Back',
    takenBy: 'Order taken by',
    checkedBy: 'Checked by',
    dispatchedBy: 'Despatch by',
  },
  ta: {
    title: 'சமையலறை விவரம்',
    order: 'ஆர்டர்',
    outlet: 'கிளை',
    customer: 'வாடிக்கையாளர்',
    contact: 'தொடர்பு',
    orderDate: 'ஆர்டர் தேதி',
    eventDate: 'நிகழ்ச்சி தேதி',
    service: 'சேவை',
    occasion: 'நிகழ்ச்சி',
    pax: 'நபர்கள்',
    session: 'வேளை',
    packageLabel: 'தொகுப்பு',
    packageItems: 'தொகுப்பு உணவுகள்',
    extraItems: 'தனியாக ஆர்டர் செய்தவை',
    parcels: 'பார்சல்கள்',
    parcel: 'பார்சல்',
    multiParcel: 'பல பார்சல்',
    parcelsNotSet: 'பார்சல் விவரம் இன்னும் இல்லை',
    address: 'விநியோக முகவரி',
    instructions: 'சிறப்பு குறிப்புகள்',
    note: 'குறிப்பு',
    live: 'நேரடி',
    leaf: 'இலை பரிமாறல்',
    buffet: 'பஃபே',
    none: 'இந்த வேளைக்கு எதுவும் இல்லை',
    nothing: 'இந்த ஆர்டரில் அனுப்ப எதுவும் இல்லை.',
    print: 'அச்சிடு',
    back: 'பின்செல்',
    takenBy: 'ஆர்டர் பெற்றவர்',
    checkedBy: 'சரிபார்த்தவர்',
    dispatchedBy: 'அனுப்பியவர்',
  },
}

/** Tamil for the sittings this business keeps; anything else keeps its own name. */
const SESSION_NAMES: Record<string, string> = {
  BREAKFAST: 'காலை டிபன்',
  LUNCH: 'மதியம் சாப்பாடு',
  SNACKS: 'சிற்றுண்டி',
  HI_TEA: 'மாலை டிபன்',
  DINNER: 'இரவு உணவு',
  MIDNIGHT: 'நள்ளிரவு உணவு',
}

const SERVICE_NAMES: Record<string, string> = {
  BANQUET: 'மண்டப நிகழ்ச்சி',
  OUTDOOR_CATERING: 'வெளியிட கேட்டரிங்',
  BULK_TAKEAWAY: 'மொத்த பார்சல்',
}

/** The hours, without the date the sheet has already given. */
function clock(value?: string | null) {
  if (!value) return ''
  const at = new Date(value)
  return Number.isNaN(at.getTime())
    ? ''
    : at.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })
}

/**
 * The dishes, named and numbered and nothing else.
 *
 * <p>No quantity beside them. The head count is at the top of the slip and the
 * kitchen cooks to it; a number repeated against every dish is a second place
 * for it to be wrong, and it crowded a half-page column that has to stay
 * readable at arm's length.</p>
 */
function DishLines({ dishes, language, text }: {
  dishes: SlipDish[]
  language: Language
  text: Record<string, string>
}) {
  return (
    <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
      {dishes.map((dish, index) => {
        // The name in the script being read, falling back to the other rather
        // than printing a gap: a dish with no Tamil name is still a dish the
        // kitchen has to cook.
        const name = (language === 'ta' ? dish.nameLocal : null) || dish.name
        return (
          <li key={`${dish.name}-${index}`}>
            <Typography variant="body2" component="span" sx={{ fontSize: '0.8rem' }}>
              {name}
              {dish.liveCooking ? (
                <Typography component="span" sx={{ fontSize: '0.7rem', ml: 0.5 }}>
                  ({text.live})
                </Typography>
              ) : null}
            </Typography>
          </li>
        )
      })}
    </Box>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.5, fontSize: '0.75rem', lineHeight: 1.5 }}>
      <Box component="span" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
        {label}:
      </Box>
      <Box component="span" sx={{ fontWeight: 500 }}>{value}</Box>
    </Box>
  )
}

/** One copy of one sitting's slip: half an A4 page, cut from its twin. */
function Copy({ slip, sitting, language, companyName }: {
  slip: KitchenSlip
  sitting: SlipSitting
  language: Language
  companyName?: string
}) {
  const text = TEXT[language]
  const sessionName =
    language === 'ta' && sitting.mealPeriod
      ? SESSION_NAMES[sitting.mealPeriod] ?? sitting.sessionName ?? humanise(sitting.mealPeriod)
      : sitting.sessionName ?? (sitting.mealPeriod ? humanise(sitting.mealPeriod) : '')
  const packageName = (language === 'ta' ? sitting.packageNameLocal : null) || sitting.packageName
  const serviceName =
    language === 'ta'
      ? SERVICE_NAMES[slip.serviceType] ?? humanise(slip.serviceType)
      : humanise(slip.serviceType)

  return (
    <Box
      className="slip-copy"
      sx={{
        flex: '1 1 0',
        minWidth: 0,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.25,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack alignItems="center" sx={{ mb: 0.5 }}>
        {companyName ? (
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{companyName}</Typography>
        ) : null}
        <Typography sx={{ fontSize: '0.8rem' }}>{text.title}</Typography>
      </Stack>
      <Divider sx={{ mb: 0.75 }} />

      <Stack
        direction="row"
        spacing={1}
        alignItems="baseline"
        justifyContent="space-between"
        sx={{ mb: 0.5 }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{sessionName}</Typography>
        <Typography sx={{ fontSize: '0.8rem' }}>
          {clock(sitting.startsAt)}
          {sitting.endsAt ? `–${clock(sitting.endsAt)}` : ''}
        </Typography>
      </Stack>

      {/* Big, because this is the one thing on the sheet that changes how the
          food is packed rather than how it is cooked. A sitting split into
          parcels and packed as one box is a job that has to be redone at the
          door. */}
      {sitting.multiParcel ? (
        <Box
          sx={{
            border: '2px solid',
            borderColor: 'text.primary',
            borderRadius: 1,
            textAlign: 'center',
            py: 0.5,
            mb: 0.75,
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', letterSpacing: '0.08em' }}>
            {text.multiParcel}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem' }}>
            {sitting.parcels.length > 0
              ? `${sitting.parcels.length} × ${text.parcel}`
              : text.parcelsNotSet}
          </Typography>
        </Box>
      ) : null}

      <Fact label={text.order} value={slip.orderNumber} />
      <Fact label={text.outlet} value={slip.outletName ?? '—'} />
      <Fact label={text.customer} value={slip.customerName} />
      <Fact
        label={text.contact}
        value={
          [slip.contactPersonName, slip.contactPhone ?? slip.customerMobile]
            .filter(Boolean)
            .join(' · ') || '—'
        }
      />
      <Fact label={text.orderDate} value={formatDate(slip.orderDate)} />
      <Fact label={text.eventDate} value={formatDate(slip.eventDate)} />
      <Fact label={text.service} value={serviceName} />
      <Fact
        label={text.pax}
        value={`${sitting.pax}${
          sitting.serviceStyle
            ? ` · ${sitting.serviceStyle === 'LEAF' ? text.leaf : text.buffet}`
            : ''
        }`}
      />
      {packageName ? <Fact label={text.packageLabel} value={packageName} /> : null}
      {slip.deliveryAddress ? <Fact label={text.address} value={slip.deliveryAddress} /> : null}
      {slip.specialInstructions ? (
        <Fact label={text.instructions} value={slip.specialInstructions} />
      ) : null}

      <Divider sx={{ my: 0.75 }} />

      {sitting.packageDishes.length === 0 && sitting.extraDishes.length === 0 ? (
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{text.none}</Typography>
      ) : null}

      {sitting.packageDishes.length > 0 ? (
        <Box>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
            {text.packageItems}
          </Typography>
          <DishLines dishes={sitting.packageDishes} language={language} text={text} />
        </Box>
      ) : null}

      {/* Kept apart from the package's own dishes. One is cooked to the package
          and the other is the thing that gets forgotten. */}
      {sitting.extraDishes.length > 0 ? (
        <Box sx={{ mt: 0.75 }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
            {text.extraItems}
          </Typography>
          <DishLines dishes={sitting.extraDishes} language={language} text={text} />
        </Box>
      ) : null}

      {sitting.parcels.length > 0 ? (
        <Box sx={{ mt: 0.75 }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
            {text.parcels}
          </Typography>
          {sitting.parcels.map((parcel) => (
            <Typography key={parcel.sequenceNo} sx={{ fontSize: '0.75rem' }}>
              {text.parcel} {parcel.sequenceNo} · {text.pax} {parcel.pax}
              {parcel.scheduledAt ? ` · ${clock(parcel.scheduledAt)}` : ''}
              {parcel.deliveryAddress ? ` · ${parcel.deliveryAddress}` : ''}
              {parcel.contactName ? ` · ${parcel.contactName}` : ''}
              {parcel.contactMobile ? ` ${parcel.contactMobile}` : ''}
              {parcel.packingNote ? ` · ${text.note}: ${parcel.packingNote}` : ''}
            </Typography>
          ))}
        </Box>
      ) : null}

      {/* Pushed to the foot of the copy, so both halves sign on the same line
          however long the menu is. */}
      <Box sx={{ flexGrow: 1, minHeight: 24 }} />
      <Stack spacing={1.5} sx={{ mt: 1 }}>
        {[text.takenBy, text.checkedBy, text.dispatchedBy].map((who) => (
          <Box key={who} sx={{ borderTop: '1px dotted', borderColor: 'text.disabled', pt: 0.25 }}>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{who}</Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  )
}

export default function KitchenSlipPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const language: Language = searchParams.get('lang') === 'ta' ? 'ta' : 'en'
  const text = TEXT[language]

  const { data: slip, isLoading, error } = useKitchenSlip(id)
  const activeCompanyId = useAuthStore((state) => state.activeCompanyId)
  const { data: companies } = useCompanies()
  const company = (companies ?? []).find((one) => one.id === activeCompanyId)

  return (
    <Box sx={{ p: 2 }}>
      <GlobalStyles
        styles={{
          // A4 upright, one sitting to a page, cut down the middle into two.
          '@page': { size: 'A4 portrait', margin: '8mm' },
          '@media print': {
            '.no-print': { display: 'none !important' },
            body: { background: '#fff' },
            '.slip-sheet': {
              breakAfter: 'page',
              breakInside: 'avoid',
              height: '277mm',
              margin: 0,
            },
            '.slip-sheet:last-of-type': { breakAfter: 'auto' },
            '.slip-copy': { height: '100%' },
          },
        }}
      />

      <Stack direction="row" spacing={1} className="no-print" sx={{ mb: 2 }} alignItems="center">
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/orders/${id}`)}>
          {text.back}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={language}
          onChange={(_event, next: Language | null) => {
            if (next) setSearchParams({ lang: next })
          }}
        >
          <ToggleButton value="en">English</ToggleButton>
          <ToggleButton value="ta">தமிழ்</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
          {text.print}
        </Button>
      </Stack>

      <ErrorBanner error={error} />
      {isLoading ? <InlineSpinner /> : null}

      {slip && slip.sittings.length === 0 ? <EmptyState message={text.nothing} /> : null}

      {slip?.sittings.map((sitting, index) => (
        // One sheet per sitting, and the same slip twice on it: one copy goes
        // with the food and one stays behind.
        <Box
          key={index}
          className="slip-sheet"
          sx={{
            display: 'flex',
            gap: 1.5,
            mb: 3,
            mx: 'auto',
            maxWidth: 900,
            alignItems: 'stretch',
          }}
        >
          <Copy slip={slip} sitting={sitting} language={language} companyName={company?.name} />
          <Copy slip={slip} sitting={sitting} language={language} companyName={company?.name} />
        </Box>
      ))}
    </Box>
  )
}
